import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CalendarPlusIcon, CheckIcon, ClockIcon, CreditCardIcon, HeartPulseIcon, MapPinIcon, PillIcon, SearchIcon, SparklesIcon, UsersIcon } from 'lucide-react';
import { declaredAllergies, inscriptionCategories, inscriptionChild, type ActivitySlot } from '../../data/loisirs';
import type { InscriptionKind } from '../../hooks/useInscriptionConversation';
interface InscriptionMessageProps {
  kind: InscriptionKind;
  live: boolean;
  category: string | null;
  slots: ActivitySlot[];
  slot: ActivitySlot | null;
  newAllergy: string | null;
  onPickCategory: (category: string) => void;
  onRegister: (slot: ActivitySlot) => void;
  onConfirmAllergies: (declaration: string | null) => void;
  onRestart: () => void;
}
const eyebrow = 'flex items-center gap-2 text-sm font-bold text-ville-indigo';
const chipButton = 'rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
function SlotDetails({
  slot
}: {
  slot: ActivitySlot;
}) {
  return <dl className="mt-2 space-y-1 text-[13px] text-slate-600" data-id="element-1890" data-mp="ywhtv2luvk2m">
      <div className="flex items-center gap-1.5" data-id="element-1891" data-mp="tuipbpedqh43">
        <ClockIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1892" data-mp="ow7htlbk4ip1" />
        <dt className="sr-only" data-id="element-1893" data-mp="oy23x2ykegup">Horaire</dt>
        <dd data-id="element-1894" data-mp="k5914kmnpil9">
          {slot.day} · {slot.time}
        </dd>
      </div>
      <div className="flex items-center gap-1.5" data-id="element-1895" data-mp="g3nq74tonedp">
        <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1896" data-mp="g4f8xf26lhmk" />
        <dt className="sr-only" data-id="element-1897" data-mp="b63z34bvz74c">Lieu</dt>
        <dd data-id="element-1898" data-mp="b2zpfrfnjj6a">{slot.place}</dd>
      </div>
      <div className="flex items-center gap-1.5" data-id="element-1899" data-mp="65zileazace4">
        <UsersIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1900" data-mp="67u7f3pdeonh" />
        <dt className="sr-only" data-id="element-1901" data-mp="17eq611ywx08">Âge et places</dt>
        <dd data-id="element-1902" data-mp="3nnvjzwieoor0">
          {slot.audience} · {slot.spots}
        </dd>
      </div>
    </dl>;
}
export function InscriptionMessage({
  kind,
  live,
  category,
  slots,
  slot,
  newAllergy,
  onPickCategory,
  onRegister,
  onConfirmAllergies,
  onRestart
}: InscriptionMessageProps) {
  if (kind === 'activite') {
    return <div data-id="element-1903" data-mp="3iq3nep6tgegh">
        <p className={eyebrow} data-id="element-1904" data-mp="3iplsbvvw46a5">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-1905" data-mp="a9igrf1npxgj" />
          Inscription
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1906" data-mp="f8lmz2tdi9q3">Quelle activité voulez-vous ?</p>
        <p className="mt-1 text-sm text-slate-500" data-id="element-1907" data-mp="1h6snzu0mm3q">
          Je cherche les places libres pour {inscriptionChild.name} ({inscriptionChild.age}).
        </p>
        {live && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-1908" data-mp="5gpd6uhxcv61">
            {inscriptionCategories.map(option => <li key={option} data-id="element-1909" data-mp="t6jov8qicdop">
                <button type="button" onClick={() => onPickCategory(option)} className={chipButton} data-id="element-1910" data-mp="xzcudytsvq1u">
                  {option}
                </button>
              </li>)}
          </ul>}
      </div>;
  }
  if (kind === 'resultats') {
    const [best, ...others] = slots;
    if (!best) {
      return <div data-id="element-1911" data-mp="2ses2fz0izpo1">
          <p className={eyebrow} data-id="element-1912" data-mp="2sejr4bk1wsey">
            <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-1913" data-mp="2ngi79oeswp2f" />
            Recherche
          </p>
          <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1914" data-mp="1zqe1bae54d89">
            Aucune place trouvée pour cette activité.
          </p>
          <p className="mt-1 text-sm text-slate-500" data-id="element-1915" data-mp="1usuo8dt6i6hw">Nommez une autre activité et je regarde de nouveau.</p>
        </div>;
    }
    return <div data-id="element-1916" data-mp="24pqojw8xbmbd">
        <p className={eyebrow} data-id="element-1917" data-mp="24q0drhm59pg0">
          <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-1918" data-mp="24opftyfdrbwt" />
          {slots.length} places trouvées en {category}
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1919" data-mp="3l25txhlgft0r">
          Voici ce que je recommande pour {inscriptionChild.name}
        </p>

        <article className="mt-4 rounded-2xl border-2 border-ville-navy bg-ville-sand p-4" data-id="element-1920" data-mp="1pobo8jyaqssw">
          <div className="flex flex-wrap items-center gap-2" data-id="element-1921" data-mp="1umd8v9bdf06h">
            <span className="rounded-full bg-ville-lime px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-ville-navy" data-id="element-1922" data-mp="1uozh7b635jpr">
              Recommandé
            </span>
            <p className="text-[15px] font-extrabold text-ville-navy" data-id="element-1923" data-mp="23p4o7k7dag3e">{best.name}</p>
          </div>
          <SlotDetails slot={best} data-id="element-1924" data-mp="28m4spqokjase" />
          {best.note && <p className="mt-2 text-[13px] font-semibold text-ville-deep" data-id="element-1925" data-mp="2dlhgqer6enwk">{best.note}</p>}
          <p className="mt-2 text-[13px] font-bold text-ville-navy" data-id="element-1926" data-mp="2icfx1ec5zlzt">{best.price}</p>
          {live && <button type="button" onClick={() => onRegister(best)} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 sm:w-auto" data-id="element-1927" data-mp="2mdt6dyegu7bh">
              <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1928" data-mp="31gb2z61wfbnl" />
              Inscrire {inscriptionChild.name} — 1 clic
            </button>}
        </article>

        {others.length > 0 && <ul className="mt-3 space-y-2" data-id="element-1929" data-mp="2shpkfawffwmt">
            {others.map(option => <li key={option.id} data-id="element-1930" data-mp="3p5ltzvtg5roj">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 px-4 py-3" data-id="element-1931" data-mp="3p5vpu016at25">
                  <div className="min-w-0" data-id="element-1932" data-mp="3fglumbb18u9t">
                    <p className="text-sm font-bold text-ville-navy" data-id="element-1933" data-mp="3ffj2k01z4lsb">
                      {option.day} · {option.time}
                    </p>
                    <p className="truncate text-xs text-slate-500" data-id="element-1934" data-mp="brquew1owgw0">
                      {option.place} · {option.spots}
                    </p>
                  </div>
                  {live && <button type="button" onClick={() => onRegister(option)} className={chipButton} data-id="element-1935" data-mp="218w5cg34r5ty">
                      Choisir
                    </button>}
                </div>
              </li>)}
          </ul>}
      </div>;
  }
  if (kind === 'allergies') {
    return <div data-id="element-1936" data-mp="1whnv5htz7gfq">
        <p className={eyebrow} data-id="element-1937" data-mp="1whe925c1xtfb">
          <HeartPulseIcon className="h-4 w-4" aria-hidden="true" data-id="element-1938" data-mp="1wgcuvrzqoqtd" />
          Dossier médical
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1939" data-mp="1ih3gmxv3g6s1">
          Y a-t-il de nouvelles allergies à déclarer pour {inscriptionChild.name} ?
        </p>
        <p className="mt-1 text-sm text-slate-500" data-id="element-1940" data-mp="18la59ht42jbu">
          Voici ce qui est déjà au dossier. Les monitrices y ont accès pendant l’activité.
        </p>

        <ul className="mt-4 space-y-2" data-id="element-1941" data-mp="1dl4snbdogqs5">
          {declaredAllergies.map(allergy => <li key={allergy.id} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-ville-sand px-4 py-3" data-id="element-1942" data-mp="32wf3g6lfi3js">
              <PillIcon className="mt-0.5 h-4 w-4 shrink-0 text-ville-deep" aria-hidden="true" data-id="element-1943" data-mp="wais6sr765y4" />
              <div className="min-w-0" data-id="element-1944" data-mp="rchbgeh8uipt">
                <p className="text-sm font-bold text-ville-navy" data-id="element-1945" data-mp="15zska8m1jom8">
                  {allergy.name}
                  <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-500" data-id="element-1946" data-mp="1192if5ctq3wa">
                    {allergy.kind}
                  </span>
                </p>
                <p className="mt-0.5 text-[13px] text-slate-600" data-id="element-1947" data-mp="1azez2mhipcx5">{allergy.detail}</p>
              </div>
            </li>)}
        </ul>

        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-1948" data-mp="1owka70w391z9">
            <button type="button" onClick={() => onConfirmAllergies(null)} className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1949" data-mp="1k0v92t4f7d0u">
              <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1950" data-mp="34v9idno3sns6" />
              Rien de nouveau
            </button>
            <p className="text-[13px] text-slate-500" data-id="element-1951" data-mp="39pnj85ljbn48">
              Sinon, écrivez la nouvelle allergie dans le champ ci-dessous.
            </p>
          </div>}
      </div>;
  }
  return <div data-id="element-1952" data-mp="2h0zs3te3u3ir">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-1953" data-mp="2ly00wc2c9kdk">
        <CheckIcon className="h-6 w-6" data-id="element-1954" data-mp="2qw1hmqcal7lv" />
      </span>
      <h3 className="mt-3 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-1955" data-mp="2vw61mqceskgu">
        {inscriptionChild.name} est inscrite
      </h3>
      {slot && <>
          <p className="mt-1 text-[15px] font-bold text-ville-navy" data-id="element-1956" data-mp="66kkciinpfap">{slot.name}</p>
          <SlotDetails slot={slot} data-id="element-1957" data-mp="b4lziscyougw" />
          <p className="mt-3 flex items-center gap-1.5 text-[13px] text-slate-600" data-id="element-1958" data-mp="b7rtakwjckiy">
            <CreditCardIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1959" data-mp="1vq2a46uk5yhh" />
            {slot.price} · payé avec la carte enregistrée
          </p>
          <p className="mt-1 text-xs text-slate-500" data-id="element-1960" data-mp="1lx4o3qqcxre4">
            Confirmation envoyée par courriel · place réservée avec la carte citoyenne de la famille
          </p>
          <p className="mt-3 flex items-start gap-1.5 text-[13px] text-slate-600" data-id="element-1961" data-mp="1bzqkwstg2z3r">
            <HeartPulseIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1962" data-mp="17yepu7rsxlo6" />
            {newAllergy ? `Nouvelle allergie ajoutée au dossier : ${newAllergy}` : `Dossier médical inchangé (${declaredAllergies.map(allergy => allergy.name).join(', ')})`}
          </p>
        </>}
      {live && <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-1963" data-mp="2xc0do7qjld9g">
          <Link to="/mes-loisirs" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-1964" data-mp="2sdqgjrl9c2l7">
            Voir mes activités
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1965" data-mp="2ij9hj3atuwrw" />
          </Link>
          <button type="button" className={`inline-flex items-center gap-2 ${chipButton}`} data-id="element-1966" data-mp="2ih5dmlcxhxrm">
            <CalendarPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1967" data-mp="2efjw2mpg8vb3" />
            Ajouter au calendrier
          </button>
          <button type="button" onClick={onRestart} className={chipButton} data-id="element-1968" data-mp="1lsz8lxc3h3ct">
            Inscrire à autre chose
          </button>
        </div>}
    </div>;
}